/**
 * SystemsModule — CRUD completo para la jerarquía:
 *   Sistema → Versión → Requerimiento → Escenario → Caso → Pasos/Acciones
 *
 * Versión es clonable — genera un espejo independiente de todo el árbol
 * (requerimientos, escenarios, casos, pasos, acciones y POM) al momento
 * de la copia. Layout: 6 columnas + panel de detalle del caso.
 * El generador de IA está en el nivel de Escenario.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStore } from '../../store';
import {
  systems as systemsApi,
  versions as versionsApi,
  requirements as reqApi,
  scenarios as scenariosApi,
  cases as casesApi,
  steps as stepsApi,
} from '../../services/api';
import { StepEditor } from '../editor/StepEditor';
import { AICaseGenerator } from '../cases/AICaseGenerator';
import { DeleteImpactModal, type ImpactData } from '../common/DeleteImpactModal';
import { ExcelImportModal } from '../cases/ExcelImportModal';
import { ExportModal } from '../cases/ExportModal';
import { JiraImportModal } from './JiraImportModal';
import DatasetManager from '../cases/DatasetManager';
import { datasetApi } from '../../services/api';
import type { TestSystem, Version, Requirement, Scenario, TestCase, TestStep } from '../../types';

// ─── Shared UI ────────────────────────────────────────────────────────────────

const PRIORITY_CFG: Record<string, { color: string; bg: string; label: string }> = {
  critical: { color: '#A32D2D', bg: '#FCEBEB', label: 'Crítica' },
  high:     { color: '#854F0B', bg: '#FAEEDA', label: 'Alta' },
  medium:   { color: '#185FA5', bg: '#E6F1FB', label: 'Media' },
  low:      { color: '#5F5E5A', bg: '#F1EFE8', label: 'Baja' },
};

const STATUS_CFG: Record<string, { color: string; bg: string }> = {
  active:     { color: '#0F6E56', bg: '#E1F5EE' },
  draft:      { color: '#854F0B', bg: '#FAEEDA' },
  deprecated: { color: '#5F5E5A', bg: '#F1EFE8' },
};

function Badge({ text, color, bg }: { text: string; color: string; bg: string }) {
  return <span style={{ padding: '2px 8px', borderRadius: 6, background: bg, color, fontSize: 10, fontWeight: 700 }}>{text}</span>;
}

function Spinner() {
  return <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12 }}>Cargando...</div>;
}

function EmptyState({ icon, text, action }: { icon: string; text: string; action?: React.ReactNode }) {
  return (
    <div style={{ padding: '36px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: 'var(--color-text-tertiary)' }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <span style={{ fontSize: 12, textAlign: 'center' }}>{text}</span>
      {action}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 480, maxHeight: '85vh', borderRadius: 16, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>{title}</span>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const INPUT: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-secondary)',
  color: 'var(--color-text-primary)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

function SmallBtn({ label, onClick, primary = false }: { label: string; onClick: () => void; primary?: boolean }) {
  return (
    <button onClick={onClick} style={{ padding: '5px 10px', borderRadius: 6, border: primary ? 'none' : '1px solid var(--color-border-secondary)', background: primary ? '#3B8BD4' : 'transparent', color: primary ? '#fff' : 'var(--color-text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
      {label}
    </button>
  );
}

// ─── Column Header ─────────────────────────────────────────────────────────

// ─── Search ────────────────────────────────────────────────────────────────

function SearchInput({ value, onChange, placeholder = 'Buscar...' }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div style={{ position: 'relative', margin: '0 8px 8px' }}>
      <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--color-text-tertiary)', pointerEvents: 'none' }}>🔍</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '5px 8px 5px 24px', fontSize: 11, borderRadius: 6,
          border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
          color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }} />
      {value && <button onClick={() => onChange('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-tertiary)', padding: 0, lineHeight: 1 }}>✕</button>}
    </div>
  );
}

// ─── Criticality ───────────────────────────────────────────────────────────

const CRITICALITY_CFG: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: 'Crítica', color: '#7A1515', bg: '#FCEBEB' },
  high:     { label: 'Alta',    color: '#7A4515', bg: '#FEF2E7' },
  medium:   { label: 'Media',   color: '#3B5FAD', bg: '#EBF0FC' },
  low:      { label: 'Baja',    color: '#0F6E56', bg: '#E1F5EE' },
};

function CriticalityBadge({ value }: { value: string }) {
  const cfg = CRITICALITY_CFG[value] ?? CRITICALITY_CFG.medium;
  return <span style={{ padding: '1px 7px', borderRadius: 5, background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700 }}>{cfg.label}</span>;
}

function ColHeader({ label, onNew, disabled }: { label: string; onNew?: () => void; disabled?: boolean }) {
  const canEdit = useAppStore(s => s.currentUserRole) !== 'viewer';
  return (
    <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {onNew && !disabled && canEdit && (
        <button onClick={onNew} style={{ border: 'none', background: '#3B8BD4', color: '#fff', borderRadius: 5, padding: '2px 7px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>+ Nuevo</button>
      )}
    </div>
  );
}

// ─── Row Components ────────────────────────────────────────────────────────

function ItemRow({ label, sublabel, selected, onClick, onEdit, onDelete, badge, icon }: {
  label: string; sublabel?: string; selected: boolean;
  onClick: () => void; onEdit: () => void; onDelete: () => void;
  badge?: React.ReactNode; icon?: string;
}) {
  const canEdit = useAppStore(s => s.currentUserRole) !== 'viewer';
  return (
    <div onClick={onClick} style={{
      padding: '10px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
      border: `1px solid ${selected ? '#3B8BD4' : 'var(--color-border-tertiary)'}`,
      background: selected ? '#E6F1FB22' : 'var(--color-background-primary)',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        {icon && <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>{icon}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
          {sublabel && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sublabel}</div>}
          {badge && <div style={{ marginTop: 4 }}>{badge}</div>}
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {canEdit && <button onClick={onEdit} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✎</button>}
          {canEdit && <button onClick={onDelete} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✕</button>}
        </div>
      </div>
    </div>
  );
}

// ─── Form Modals ───────────────────────────────────────────────────────────

const PLATFORM_OPTIONS: { value: TestSystem['platform']; label: string; icon: string }[] = [
  { value: 'web',     label: 'Web',     icon: '🌐' },
  { value: 'mobile',  label: 'Móvil',   icon: '📱' },
  { value: 'desktop', label: 'Desktop', icon: '🖥' },
  { value: 'api',     label: 'API',     icon: '⚡' },
];

function SystemModal({ initial, onSave, onClose }: { initial?: Partial<TestSystem>; onSave: (d: Partial<TestSystem>) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [platform, setPlatform] = useState<TestSystem['platform']>(initial?.platform ?? undefined);
  const [version, setVersion] = useState(initial?.version ?? '');
  const [url, setUrl] = useState(initial?.base_url ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name, description: desc || undefined, platform, version: version || undefined, base_url: url || undefined });
    setSaving(false);
    onClose();
  };
  return (
    <Modal title={initial?.id ? 'Editar sistema' : 'Nuevo sistema'} onClose={onClose}>
      <Field label="Nombre *">
        <input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="Portal Web" />
      </Field>
      <Field label="Plataforma">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PLATFORM_OPTIONS.map(opt => {
            const active = platform === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setPlatform(active ? undefined : opt.value)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                  border: `2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                  background: active ? 'var(--accent)' : 'var(--surface)',
                  color: active ? '#fff' : 'var(--text)',
                  fontWeight: active ? 600 : 400,
                  transition: 'all 0.15s',
                }}
              >
                <span>{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Descripción">
        <textarea
          style={{ ...INPUT, resize: 'vertical' }}
          rows={3}
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="Describe el sistema, sus módulos principales y reglas de negocio clave. Este texto se usa como contexto base para la generación de casos con IA."
        />
      </Field>
      <Field label="Versión">
        <input
          style={INPUT}
          value={version}
          onChange={e => setVersion(e.target.value)}
          placeholder="ej. 2.1.0, v3-beta, sprint-42"
          maxLength={32}
        />
      </Field>
      <Field label="URL base">
        <input style={INPUT} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://app.example.com" />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <SmallBtn label="Cancelar" onClick={onClose} />
        <SmallBtn label={saving ? 'Guardando...' : 'Guardar'} onClick={save} primary />
      </div>
    </Modal>
  );
}

function RequirementModal({ initial, onSave, onClose }: { initial?: Partial<Requirement>; onSave: (d: Partial<Requirement>) => Promise<void>; onClose: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [priority, setPriority] = useState(initial?.priority ?? 'medium');
  const [extRef, setExtRef] = useState(initial?.external_ref ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!title.trim()) return; setSaving(true); await onSave({ title, description: desc, priority, external_ref: extRef }); setSaving(false); onClose(); };
  return (
    <Modal title={initial?.id ? 'Editar requerimiento' : 'Nuevo requerimiento'} onClose={onClose}>
      <Field label="Título *"><input style={INPUT} value={title} onChange={e => setTitle(e.target.value)} placeholder="El usuario debe poder iniciar sesión" /></Field>
      <Field label="Descripción"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={desc} onChange={e => setDesc(e.target.value)} /></Field>
      <Field label="Prioridad">
        <select style={INPUT} value={priority} onChange={e => setPriority(e.target.value as 'low' | 'medium' | 'high' | 'critical')}>
          {Object.entries(PRIORITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </Field>
      <Field label="Referencia (Jira, etc.)"><input style={INPUT} value={extRef} onChange={e => setExtRef(e.target.value)} placeholder="PROJ-123" /></Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <SmallBtn label="Cancelar" onClick={onClose} />
        <SmallBtn label={saving ? 'Guardando...' : 'Guardar'} onClick={save} primary />
      </div>
    </Modal>
  );
}

const VERSION_STATUS_CFG: Record<string, { color: string; bg: string; label: string }> = {
  draft:    { color: '#854F0B', bg: '#FAEEDA', label: 'Borrador' },
  active:   { color: '#0F6E56', bg: '#E1F5EE', label: 'Activa' },
  archived: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Archivada' },
};

function VersionModal({ initial, onSave, onClose }: { initial?: Partial<Version>; onSave: (d: Partial<Version>) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!name.trim()) return; setSaving(true); await onSave({ name, description: desc || undefined }); setSaving(false); onClose(); };
  return (
    <Modal title={initial?.id ? 'Editar versión' : 'Nueva versión'} onClose={onClose}>
      <Field label="Nombre *"><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="v2.1.0, Sprint 14..." /></Field>
      <Field label="Descripción"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={desc} onChange={e => setDesc(e.target.value)} /></Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <SmallBtn label="Cancelar" onClick={onClose} />
        <SmallBtn label={saving ? 'Guardando...' : 'Guardar'} onClick={save} primary />
      </div>
    </Modal>
  );
}

function ScenarioModal({ initial, onSave, onClose }: { initial?: Partial<Scenario>; onSave: (d: Partial<Scenario>) => Promise<void>; onClose: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => { if (!title.trim()) return; setSaving(true); await onSave({ title, description: desc }); setSaving(false); onClose(); };
  return (
    <Modal title={initial?.id ? 'Editar escenario' : 'Nuevo escenario'} onClose={onClose}>
      <Field label="Título *"><input style={INPUT} value={title} onChange={e => setTitle(e.target.value)} placeholder="Login con credenciales válidas" /></Field>
      <Field label="Descripción"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={3} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe el flujo o criterios de aceptación de este escenario" /></Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <SmallBtn label="Cancelar" onClick={onClose} />
        <SmallBtn label={saving ? 'Guardando...' : 'Guardar'} onClick={save} primary />
      </div>
    </Modal>
  );
}

function CaseModal({ initial, onSave, onClose }: { initial?: Partial<TestCase>; onSave: (d: Partial<TestCase>) => Promise<void>; onClose: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [status, setStatus] = useState(initial?.status ?? 'draft');
  const [tags, setTags] = useState((initial?.tags ?? []).join(', '));
  const VALID_CRIT = new Set(['critical', 'high', 'medium', 'low']);
  const initCrit = initial?.criticality && VALID_CRIT.has(initial.criticality) ? initial.criticality : 'medium';
  const [criticality, setCriticality] = useState<'critical' | 'high' | 'medium' | 'low'>(initCrit as 'critical' | 'high' | 'medium' | 'low');
  const [preconditions, setPreconditions] = useState(initial?.preconditions ?? '');
  const [postconditions, setPostconditions] = useState(initial?.postconditions ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    await onSave({ title, description: desc, status, tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      criticality, preconditions: preconditions.trim() || undefined, postconditions: postconditions.trim() || undefined });
    setSaving(false); onClose();
  };
  return (
    <Modal title={initial?.id ? 'Editar caso' : 'Nuevo caso de prueba'} onClose={onClose}>
      <Field label="Título *"><input style={INPUT} value={title} onChange={e => setTitle(e.target.value)} placeholder="Login exitoso con credenciales válidas" /></Field>
      <Field label="Descripción"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={desc} onChange={e => setDesc(e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Estado">
          <select style={INPUT} value={status} onChange={e => setStatus(e.target.value as 'draft' | 'active' | 'deprecated')}>
            <option value="draft">Borrador</option><option value="active">Activo</option><option value="deprecated">Deprecado</option>
          </select>
        </Field>
        <Field label="Criticidad">
          <select style={INPUT} value={criticality} onChange={e => setCriticality(e.target.value as 'critical' | 'high' | 'medium' | 'low')}>
            <option value="critical">Crítica</option><option value="high">Alta</option><option value="medium">Media</option><option value="low">Baja</option>
          </select>
        </Field>
      </div>
      <Field label="Tags (coma)"><input style={INPUT} value={tags} onChange={e => setTags(e.target.value)} placeholder="smoke, regression" /></Field>
      <Field label="Precondiciones"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={preconditions} onChange={e => setPreconditions(e.target.value)} placeholder="Estado del sistema antes de ejecutar el caso..." /></Field>
      <Field label="Postcondiciones"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={postconditions} onChange={e => setPostconditions(e.target.value)} placeholder="Estado esperado del sistema después de la ejecución..." /></Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <SmallBtn label="Cancelar" onClick={onClose} />
        <SmallBtn label={saving ? 'Guardando...' : 'Guardar'} onClick={save} primary />
      </div>
    </Modal>
  );
}

// ─── Case Detail Panel ─────────────────────────────────────────────────────

function CaseDetailPanel({ tc, scenarioId, scenarioTitle, scenarioDatasetColumns, onClose, onCasesCreated, versionId }: {
  tc: TestCase; scenarioId: string; scenarioTitle: string; scenarioDatasetColumns?: string[];
  onClose: () => void; onCasesCreated: () => void; versionId?: string;
}) {
  const [steps, setSteps] = useState<TestStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'steps' | 'mapping'>('steps');
  // Inicializar con columns del escenario padre — evita parpadeo "Sin dataset"
  const [datasetColumns, setDatasetColumns] = useState<string[]>(scenarioDatasetColumns ?? []);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [savingMapping, setSavingMapping] = useState(false);

  // Cargar dataset columns y mapping al abrir — refresca desde BD
  React.useEffect(() => {
    Promise.all([
      datasetApi.getDataset(scenarioId).catch(() => null),
      datasetApi.getDatasetMapping(tc.id).catch(() => ({ mapping: {} })),
    ]).then(([ds, mp]) => {
      const dsTyped = ds as { columns?: string[] } | null;
      // Actualizar columns solo si BD tiene datos (evita borrar las del prop)
      if (dsTyped?.columns?.length) setDatasetColumns(dsTyped.columns);
      const mpTyped = mp as { mapping: Record<string, string> };
      setMapping(mpTyped?.mapping || {});
    });
  }, [tc.id, scenarioId]);

  const handleMappingChange = (actionId: string, column: string) => {
    setMapping(prev => {
      const next = { ...prev };
      if (column) next[actionId] = column; else delete next[actionId];
      return next;
    });
  };

  const saveMapping = async () => {
    setSavingMapping(true);
    try {
      await datasetApi.saveDatasetMapping(tc.id, mapping);
    } finally {
      setSavingMapping(false);
    }
  };

  const loadSteps = useCallback(() => {
    setLoading(true);
    stepsApi.list(tc.id)
      .then(s => setSteps(s.map(step => ({
        ...step,
        actions: (step.actions ?? []).map((a) => {
          const r = a as unknown as Record<string, unknown>;
          return {
            action_id: (r.action_id ?? r.id ?? String(Date.now())) as string,
            type: (r.type ?? r.action_type ?? 'CLICK') as import('../../types').ActionType,
            target: (r.target ?? { primary: '', fallbacks: [], strategy: 'css' }) as import('../../types').ActionTarget,
            params: (r.params ?? {}) as Record<string, unknown>,
            on_failure: (r.on_failure ?? 'abort') as import('../../types').OnFailure,
            meta: (r.meta ?? { created_by: 'human', description: '', healing_history: [], tags: [] }) as import('../../types').ActionMeta,
          } as import('../../types').DSLAction;
        }),
      }))))
      .finally(() => setLoading(false));
  }, [tc.id]);

  useEffect(() => { loadSteps(); }, [loadSteps]);

  const sc = STATUS_CFG[tc.status] ?? STATUS_CFG.draft;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 2 }}>{scenarioTitle}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>{tc.title}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <Badge text={tc.status} color={sc.color} bg={sc.bg} />
            {tc.generated_by === 'ai' && <Badge text="IA" color="#534AB7" bg="#EEEDFE" />}
            <CriticalityBadge value={tc.criticality ?? 'medium'} />
            {tc.tags.map(t => <span key={t} style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)', fontSize: 10 }}>{t}</span>)}
          </div>
          {(tc.preconditions || tc.postconditions) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              {tc.preconditions && (
                <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Precondiciones</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{tc.preconditions}</div>
                </div>
              )}
              {tc.postconditions && (
                <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Postcondiciones</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{tc.postconditions}</div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <SmallBtn label="↺ Recargar" onClick={loadSteps} />
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border-tertiary)', padding: '0 18px' }}>
        {(['steps', 'mapping'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: activeTab === tab ? '#185FA5' : 'var(--color-text-tertiary)', borderBottom: activeTab === tab ? '2px solid #185FA5' : '2px solid transparent', marginBottom: -1 }}>
            {tab === 'steps' ? `📋 Pasos (${steps.length})` : `📊 Mapeo Dataset${datasetColumns.length ? ` (${Object.keys(mapping).length}/${datasetColumns.length})` : ''}`}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
        {activeTab === 'steps' ? (
          loading ? <Spinner /> : <StepEditor caseId={tc.id} initialSteps={steps} onStepsChange={setSteps} versionId={versionId} datasetColumns={datasetColumns} />
        ) : (
          <DatasetMappingPanel
            steps={steps}
            columns={datasetColumns}
            mapping={mapping}
            onChange={handleMappingChange}
            onSave={saveMapping}
            saving={savingMapping}
          />
        )}
      </div>
    </div>
  );
}

// ─── Dataset Mapping Panel ─────────────────────────────────────────────────

function DatasetMappingPanel({ steps, columns, mapping, onChange, onSave, saving }: {
  steps: import('../../types').TestStep[];
  columns: string[];
  mapping: Record<string, string>;
  onChange: (actionId: string, column: string) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}) {
  const [saved, setSaved] = React.useState(false);
  const handleSave = async () => {
    await onSave();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // Extraer solo acciones que tienen params.value (INPUT, SELECT, ASSERT_VALUE, etc.)
  const mappableActions: { actionId: string; stepDesc: string; actionType: string; currentValue: string }[] = [];
  for (const step of steps) {
    for (const action of step.actions || []) {
      const params = action.params as Record<string, unknown>;
      if ('value' in params) {
        mappableActions.push({
          actionId: action.action_id || String(action),
          stepDesc: step.description,
          actionType: action.type,
          currentValue: String(params.value ?? ''),
        });
      }
    }
  }

  if (!columns.length) return (
    <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-tertiary)', fontSize: 13 }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Sin dataset en este escenario</div>
      <div style={{ fontSize: 12 }}>Configura un dataset desde el panel del escenario (botón 📊 Dataset) para habilitar el mapeo.</div>
    </div>
  );

  if (!mappableActions.length) return (
    <div style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-tertiary)', fontSize: 13 }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Sin acciones mapeables</div>
      <div style={{ fontSize: 12 }}>Solo se pueden mapear acciones que tengan un campo <code>value</code> (INPUT, SELECT, ASSERT_VALUE…).</div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--color-text-tertiary)', padding: '10px 14px', background: 'var(--color-background-secondary)', borderRadius: 8, border: '1px solid var(--color-border-tertiary)' }}>
        💡 Vincula cada acción con la columna del dataset que proveerá su valor en cada iteración. Solo se muestran acciones con campo <code>value</code>.
      </div>

      <div style={{ marginBottom: 16 }}>
        {mappableActions.map(({ actionId, stepDesc, actionType, currentValue }) => (
          <div key={actionId} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center', marginBottom: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
            {/* Acción */}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase' }}>{actionType}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stepDesc}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                valor actual: {currentValue || '(vacío)'}
              </div>
            </div>

            {/* Flecha */}
            <div style={{ fontSize: 16, color: mapping[actionId] ? '#185FA5' : 'var(--color-border-secondary)', textAlign: 'center' }}>→</div>

            {/* Selector columna */}
            <select value={mapping[actionId] || ''} onChange={e => onChange(actionId, e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${mapping[actionId] ? '#185FA5' : 'var(--color-border-secondary)'}`, background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', fontSize: 12, cursor: 'pointer' }}>
              <option value="">— Sin mapeo —</option>
              {columns.map(col => <option key={col} value={col}>{col}</option>)}
            </select>
          </div>
        ))}
      </div>

      <button onClick={handleSave} disabled={saving}
        style={{ padding: '8px 20px', borderRadius: 8, background: saved ? '#22863a' : '#185FA5', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, transition: 'background 0.2s' }}>
        {saving ? 'Guardando…' : saved ? '✅ Guardado' : 'Guardar mapeo'}
      </button>
    </div>
  );
}

// ─── Scenario Detail (with AI generator) ───────────────────────────────────

function ScenarioDetailPanel({ scenario, requirementTitle, system, onClose, onCasesCreated, onSelectCase, caseList, loadingCases, onNewCase, onEditCase, onDeleteCase, onDuplicateCase, selectedCase }: {
  scenario: Scenario; requirementTitle: string; system?: TestSystem; onClose: () => void; onCasesCreated: () => void;
  onSelectCase: (tc: TestCase) => void; caseList: TestCase[]; loadingCases: boolean;
  onNewCase: () => void; onEditCase: (tc: TestCase) => void; onDeleteCase: (tc: TestCase) => void;
  onDuplicateCase: (tc: TestCase) => void; selectedCase: TestCase | null;
  onDatasetColumnsChange?: (columns: string[]) => void;
}) {
  const canEdit = useAppStore(s => s.currentUserRole) !== 'viewer';
  const [showAI, setShowAI] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [qCasesLocal, setQCasesLocal] = useState('');
  const [showDataset, setShowDataset] = useState(false);
  const [datasetRowCount, setDatasetRowCount] = useState(0);
  const [datasetColumns, setDatasetColumns] = useState<string[]>([]);

  // Cargar dataset real desde BD al montar — el badge y las columns no dependen de abrir el modal
  React.useEffect(() => {
    datasetApi.getDataset(scenario.id)
      .then(ds => {
        const typed = ds as { row_count?: number; columns?: string[] } | null;
        setDatasetRowCount(typed?.row_count ?? 0);
        setDatasetColumns(typed?.columns ?? []);
      })
      .catch(() => {});
  }, [scenario.id]);
  const filteredCaseList = qCasesLocal
    ? caseList.filter(tc => tc.title.toLowerCase().includes(qCasesLocal.toLowerCase()) || tc.tags?.some(t => t.toLowerCase().includes(qCasesLocal.toLowerCase())))
    : caseList;

  const handleAICreated = () => { setShowAI(false); onCasesCreated(); };
  const handleImported = () => { setShowImport(false); onCasesCreated(); };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginBottom: 2 }}>{requirementTitle}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>{scenario.title}</div>
          {scenario.description && <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', marginTop: 3 }}>{scenario.description}</div>}
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--color-text-tertiary)' }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* AI Generator — en el nivel de Escenario */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Generador IA
            </div>
            <button
              onClick={() => setShowAI(v => !v)}
              style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: showAI ? '#EEEDFE' : '#7F77DD', color: showAI ? '#534AB7' : '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
            >
              {showAI ? '✕ Cerrar' : '✦ Generar casos con IA'}
            </button>
          </div>
          {/* Dataset button — independiente del panel IA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <button onClick={() => setShowDataset(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: datasetRowCount > 0 ? '#e8f4fd' : 'var(--color-background-secondary)', color: datasetRowCount > 0 ? '#185FA5' : 'var(--color-text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              📊 Dataset
              {datasetRowCount > 0 && <span style={{ padding: '1px 7px', borderRadius: 20, background: '#185FA5', color: '#fff', fontSize: 10 }}>{datasetRowCount} filas</span>}
            </button>
          </div>

          {showAI && (
            <AICaseGenerator
              scenarioId={scenario.id}
              scenarioTitle={scenario.title}
              system={system}
              onCasesCreated={handleAICreated}
            />
          )}

      {showDataset && (
        <DatasetManager
          scenarioId={scenario.id}
          scenarioTitle={scenario.title}
          onClose={() => setShowDataset(false)}
          onDatasetChange={(_, count) => setDatasetRowCount(count)}
        />
      )}
      {showImport && (
        <ExcelImportModal
          scenarioId={scenario.id}
          scenarioTitle={scenario.title}
          onClose={() => setShowImport(false)}
          onImported={handleImported}
        />
      )}
      {showExport && (
        <ExportModal
          scenarioId={scenario.id}
          scenarioTitle={scenario.title}
          caseCount={caseList.length}
          onClose={() => setShowExport(false)}
        />
      )}
        </div>

        {/* Cases list */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Casos de prueba <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>({caseList.length})</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
{canEdit && <button onClick={() => setShowImport(true)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>📥 Importar</button>}
              <button onClick={() => setShowExport(true)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>📤 Exportar</button>
              {canEdit && <SmallBtn label="+ Nuevo caso" onClick={onNewCase} primary />}
            </div>
          </div>
          <SearchInput value={qCasesLocal} onChange={setQCasesLocal} placeholder="Buscar caso o tag..." />
          {loadingCases ? <Spinner />
          : filteredCaseList.length === 0
            ? <EmptyState icon="◻" text={qCasesLocal ? 'Sin resultados' : 'Sin casos — genera con IA o crea manualmente'} />
            : filteredCaseList.map(tc => {
              const sc = STATUS_CFG[tc.status] ?? STATUS_CFG.draft;
              return (
                <div key={tc.id} onClick={() => onSelectCase(tc)}
                  style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, border: `1px solid ${selectedCase?.id === tc.id ? '#3B8BD4' : 'var(--color-border-tertiary)'}`, background: selectedCase?.id === tc.id ? '#E6F1FB22' : 'var(--color-background-secondary)', transition: 'all 0.15s' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tc.title}</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <Badge text={tc.status} color={sc.color} bg={sc.bg} />
                        {tc.generated_by === 'ai' && <Badge text="IA" color="#534AB7" bg="#EEEDFE" />}
                        {tc.has_dataset && <Badge text="📊 Dataset" color="#185FA5" bg="#e8f4fd" />}
                        {tc.tags.map(t => <span key={t} style={{ padding: '1px 5px', borderRadius: 3, background: 'var(--color-background-primary)', color: 'var(--color-text-tertiary)', fontSize: 9 }}>{t}</span>)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 2 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => onDuplicateCase(tc)} title="Duplicar" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)' }}>⧉</button>
                      {canEdit && <button onClick={() => onEditCase(tc)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)' }}>✎</button>}
                      {canEdit && <button onClick={() => onDeleteCase(tc)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)' }}>✕</button>}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

// ─── Main SystemsModule ────────────────────────────────────────────────────

export function SystemsModule() {
  const [systemsList, setSystemsList] = useState<TestSystem[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<TestSystem | null>(null);
  const [systemModal, setSystemModal] = useState<'create' | TestSystem | null>(null);
  const [loadingSystems, setLoadingSystems] = useState(true);

  const [versionList, setVersionList] = useState<Version[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [versionModal, setVersionModal] = useState<'create' | Version | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const [reqList, setReqList] = useState<Requirement[]>([]);
  const [selectedReq, setSelectedReq] = useState<Requirement | null>(null);
  const [reqModal, setReqModal] = useState<'create' | Requirement | null>(null);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [showJiraImport, setShowJiraImport] = useState(false);
  const canEditReqs = useAppStore(s => s.currentUserRole) !== 'viewer';

  const [scenarioList, setScenarioList] = useState<Scenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [scenarioModal, setScenarioModal] = useState<'create' | Scenario | null>(null);
  const [loadingScenarios, setLoadingScenarios] = useState(false);

  const [caseList, setCaseList] = useState<TestCase[]>([]);
  const [selectedCase, setSelectedCase] = useState<TestCase | null>(null);
  const [scenarioDatasetColumns, setScenarioDatasetColumns] = useState<string[]>([]);

  // Limpiar columns al cambiar de escenario
  React.useEffect(() => {
    setScenarioDatasetColumns([]);
    if (!selectedScenario) return;
    datasetApi.getDataset(selectedScenario.id)
      .then(ds => {
        const typed = ds as { columns?: string[] } | null;
        setScenarioDatasetColumns(typed?.columns ?? []);
      })
      .catch(() => {});
  }, [selectedScenario?.id]);
  const [caseModal, setCaseModal] = useState<'create' | TestCase | null>(null);
  const [loadingCases, setLoadingCases] = useState(false);

  // ── Search queries ────────────────────────────────────────────────────────
  const [qSystems,   setQSystems]   = useState('');
  const [qVersions,  setQVersions]  = useState('');
  const [qReqs,      setQReqs]      = useState('');
  const [qScenarios, setQScenarios] = useState('');
  const [qCases,     setQCases]     = useState('');

  const [toast, setToast] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    entityType: 'caso' | 'escenario' | 'requerimiento' | 'version' | 'sistema';
    entityName: string;
    impact: ImpactData;
    onConfirm: () => void;
  } | null>(null);
  const notify = useCallback((msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); }, []);

  // ── Impact-aware delete ─────────────────────────────────────────────────

  const withImpactCheck = async (
    entityType: 'caso' | 'escenario' | 'requerimiento' | 'version' | 'sistema',
    entityName: string,
    getImpact: () => Promise<ImpactData>,
    onConfirm: () => Promise<void>,
  ) => {
    const impact = await getImpact();
    // Siempre usar el modal — nunca window.confirm
    setDeleteConfirm({ entityType, entityName, impact, onConfirm });
  };

  // ── Reload functions ────────────────────────────────────────────────────

  const reloadSystems = useCallback(async () => {
    setLoadingSystems(true);
    try { const s = await systemsApi.list(); setSystemsList(s); return s; }
    finally { setLoadingSystems(false); }
  }, []);

  const reloadVersions = useCallback(async (sysId: string) => {
    setLoadingVersions(true);
    try { const v = await versionsApi.list(sysId); setVersionList(v); return v; }
    finally { setLoadingVersions(false); }
  }, []);

  const reloadReqs = useCallback(async (versionId: string) => {
    setLoadingReqs(true);
    try { const r = await reqApi.list(versionId); setReqList(r); return r; }
    finally { setLoadingReqs(false); }
  }, []);

  const reloadScenarios = useCallback(async (reqId: string) => {
    setLoadingScenarios(true);
    try { const s = await scenariosApi.list(reqId); setScenarioList(s); return s; }
    finally { setLoadingScenarios(false); }
  }, []);

  const reloadCases = useCallback(async (scenarioId: string) => {
    setLoadingCases(true);
    try { const c = await casesApi.list(scenarioId); setCaseList(c); return c; }
    finally { setLoadingCases(false); }
  }, []);

  // ── Initial load ────────────────────────────────────────────────────────

  useEffect(() => { reloadSystems().then(s => { if (s.length > 0) setSelectedSystem(s[0]); }); }, []);

  useEffect(() => {
    if (!selectedSystem) { setVersionList([]); setSelectedVersion(null); setReqList([]); setSelectedReq(null); setScenarioList([]); setSelectedScenario(null); setCaseList([]); return; }
    reloadVersions(selectedSystem.id).then(v => setSelectedVersion(prev =>
      v.find(x => x.id === prev?.id) ?? v.find(x => x.status === 'active') ?? v[0] ?? null
    ));
  }, [selectedSystem?.id]);

  // Al clonar una versión, se fuerza a seleccionar su primer requerimiento
  // (en vez de intentar preservar la selección previa, que nunca calza con
  // los IDs nuevos) para que el panel derecho no quede vacío — evidencia
  // visual inmediata de que la copia se pobló correctamente.
  const autoSelectFirstReqRef = useRef(false);

  useEffect(() => {
    if (!selectedVersion) { setReqList([]); setSelectedReq(null); setScenarioList([]); setSelectedScenario(null); setCaseList([]); return; }
    reloadReqs(selectedVersion.id).then(r => {
      if (autoSelectFirstReqRef.current) {
        autoSelectFirstReqRef.current = false;
        setSelectedReq(r[0] ?? null);
      } else {
        setSelectedReq(prev => r.find(x => x.id === prev?.id) ?? null);
      }
    });
  }, [selectedVersion?.id]);

  useEffect(() => {
    if (!selectedReq) { setScenarioList([]); setSelectedScenario(null); setCaseList([]); setSelectedCase(null); return; }
    reloadScenarios(selectedReq.id).then(s => setSelectedScenario(prev => s.find(x => x.id === prev?.id) ?? null));
  }, [selectedReq?.id]);

  useEffect(() => {
    if (!selectedScenario) { setCaseList([]); setSelectedCase(null); return; }
    reloadCases(selectedScenario.id).then(c => setSelectedCase(prev => c.find(x => x.id === prev?.id) ?? null));
  }, [selectedScenario?.id]);

  // ── Systems CRUD ────────────────────────────────────────────────────────

  const handleCreateSystem = async (d: Partial<TestSystem>) => {
    const s = await systemsApi.create(d); await reloadSystems(); setSelectedSystem(s); notify('Sistema creado ✓');
  };
  const handleEditSystem = async (d: Partial<TestSystem>) => {
    if (typeof systemModal !== 'object' || !systemModal) return;
    await systemsApi.update(systemModal.id, d); const u = await reloadSystems();
    const f = u.find(x => x.id === systemModal.id); if (f && selectedSystem?.id === f.id) setSelectedSystem(f);
    notify('Sistema actualizado ✓');
  };
  const handleDeleteSystem = async (sys: TestSystem) => {
    await withImpactCheck('sistema', sys.name,
      () => systemsApi.impact(sys.id) as unknown as Promise<ImpactData>,
      async () => {
        await systemsApi.delete(sys.id);
        if (selectedSystem?.id === sys.id) { setSelectedSystem(null); setVersionList([]); setSelectedVersion(null); setReqList([]); setScenarioList([]); setCaseList([]); }
        await reloadSystems(); notify('Sistema eliminado ✓');
      }
    );
  };

  // ── Versions CRUD ───────────────────────────────────────────────────────

  const handleCreateVersion = async (d: Partial<Version>) => {
    if (!selectedSystem) return;
    const v = await versionsApi.create(selectedSystem.id, { ...d, order_index: versionList.length });
    await reloadVersions(selectedSystem.id); setSelectedVersion(v); notify('Versión creada ✓');
  };
  const handleEditVersion = async (d: Partial<Version>) => {
    if (typeof versionModal !== 'object' || !versionModal || !selectedSystem) return;
    await versionsApi.update(versionModal.id, d); const u = await reloadVersions(selectedSystem.id);
    const f = u.find(x => x.id === versionModal.id); if (f && selectedVersion?.id === f.id) setSelectedVersion(f);
    notify('Versión actualizada ✓');
  };
  const handleDeleteVersion = async (ver: Version) => {
    await withImpactCheck('version', ver.name,
      () => versionsApi.impact(ver.id) as unknown as Promise<ImpactData>,
      async () => {
        await versionsApi.delete(ver.id);
        if (selectedVersion?.id === ver.id) { setSelectedVersion(null); setReqList([]); setScenarioList([]); setCaseList([]); }
        if (selectedSystem) await reloadVersions(selectedSystem.id); notify('Versión eliminada ✓');
      }
    );
  };
  const handleActivateVersion = async (ver: Version) => {
    await versionsApi.activate(ver.id);
    if (selectedSystem) { const u = await reloadVersions(selectedSystem.id); const f = u.find(x => x.id === ver.id); if (f) setSelectedVersion(f); }
    notify('Versión activada ✓');
  };
  const handleCloneVersion = async (ver: Version) => {
    const clone = await versionsApi.clone(ver.id);
    if (selectedSystem) await reloadVersions(selectedSystem.id);
    autoSelectFirstReqRef.current = true;
    setSelectedVersion(clone);
    notify('Versión clonada ✓');
  };

  // ── Requirements CRUD ───────────────────────────────────────────────────

  const handleCreateReq = async (d: Partial<Requirement>) => {
    if (!selectedVersion) return;
    const r = await reqApi.create(selectedVersion.id, { ...d, order_index: reqList.length });
    await reloadReqs(selectedVersion.id); setSelectedReq(r); notify('Requerimiento creado ✓');
  };
  const handleEditReq = async (d: Partial<Requirement>) => {
    if (typeof reqModal !== 'object' || !reqModal || !selectedVersion) return;
    await reqApi.update(reqModal.id, d); const u = await reloadReqs(selectedVersion.id);
    const f = u.find(x => x.id === reqModal.id); if (f && selectedReq?.id === f.id) setSelectedReq(f);
    notify('Requerimiento actualizado ✓');
  };
  const handleDeleteReq = async (req: Requirement) => {
    await withImpactCheck('requerimiento', req.title,
      () => reqApi.impact(req.id) as unknown as Promise<ImpactData>,
      async () => {
        await reqApi.delete(req.id);
        if (selectedReq?.id === req.id) { setSelectedReq(null); setScenarioList([]); setCaseList([]); setSelectedCase(null); }
        if (selectedVersion) await reloadReqs(selectedVersion.id); notify('Requerimiento eliminado ✓');
      }
    );
  };

  // ── Scenarios CRUD ──────────────────────────────────────────────────────

  const handleCreateScenario = async (d: Partial<Scenario>) => {
    if (!selectedReq) return;
    const s = await scenariosApi.create(selectedReq.id, { ...d, order_index: scenarioList.length });
    await reloadScenarios(selectedReq.id); setSelectedScenario(s); notify('Escenario creado ✓');
  };
  const handleEditScenario = async (d: Partial<Scenario>) => {
    if (typeof scenarioModal !== 'object' || !scenarioModal || !selectedReq) return;
    await scenariosApi.update(scenarioModal.id, d); const u = await reloadScenarios(selectedReq.id);
    const f = u.find(x => x.id === scenarioModal.id); if (f && selectedScenario?.id === f.id) setSelectedScenario(f);
    notify('Escenario actualizado ✓');
  };
  const handleDeleteScenario = async (sc: Scenario) => {
    await withImpactCheck('escenario', sc.title,
      () => scenariosApi.impact(sc.id) as unknown as Promise<ImpactData>,
      async () => {
        await scenariosApi.delete(sc.id);
        if (selectedScenario?.id === sc.id) { setSelectedScenario(null); setCaseList([]); setSelectedCase(null); }
        if (selectedReq) await reloadScenarios(selectedReq.id); notify('Escenario eliminado ✓');
      }
    );
  };

  // ── Cases CRUD ──────────────────────────────────────────────────────────

  const handleCreateCase = async (d: Partial<TestCase>) => {
    if (!selectedScenario) return;
    const c = await casesApi.create(selectedScenario.id, { ...d, order_index: caseList.length });
    await reloadCases(selectedScenario.id); setSelectedCase(c); notify('Caso creado ✓');
  };
  const handleEditCase = async (d: Partial<TestCase>) => {
    if (typeof caseModal !== 'object' || !caseModal || !selectedScenario) return;
    await casesApi.update(caseModal.id, d); const u = await reloadCases(selectedScenario.id);
    const f = u.find(x => x.id === caseModal.id); if (f && selectedCase?.id === f.id) setSelectedCase(f);
    notify('Caso actualizado ✓');
  };
  const handleDeleteCase = async (tc: TestCase) => {
    await withImpactCheck('caso', tc.title,
      () => casesApi.impact(tc.id) as unknown as Promise<ImpactData>,
      async () => {
        await casesApi.delete(tc.id);
        if (selectedCase?.id === tc.id) setSelectedCase(null);
        if (selectedScenario) await reloadCases(selectedScenario.id); notify('Caso eliminado ✓');
      }
    );
  };
  const handleDuplicateCase = async (tc: TestCase) => {
    await casesApi.duplicate(tc.id); if (selectedScenario) await reloadCases(selectedScenario.id);
    notify(`Caso duplicado ✓`);
  };
  const handleAICasesCreated = useCallback(async () => {
    if (selectedScenario) { const u = await reloadCases(selectedScenario.id); if (u.length > 0) setSelectedCase(u[u.length - 1]); }
  }, [selectedScenario, reloadCases]);

  // ── Column renderer ─────────────────────────────────────────────────────

  // ── Filtered lists ──────────────────────────────────────────────────────
  const q = (s: string) => s.toLowerCase();
  const filteredSystems   = systemsList.filter((s: TestSystem) => !qSystems   || q(s.name).includes(q(qSystems)));
  const filteredVersions  = versionList.filter((v: Version)    => !qVersions  || q(v.name).includes(q(qVersions)));
  const filteredReqs      = reqList.filter((r: Requirement)    => !qReqs      || q(r.title).includes(q(qReqs)));
  const filteredScenarios = scenarioList.filter((s: Scenario)  => !qScenarios || q(s.title).includes(q(qScenarios)));
  const filteredCases     = caseList.filter((tc: TestCase)     => !qCases     || q(tc.title).includes(q(qCases)) || tc.tags?.some((t: string) => q(t).includes(q(qCases))));

  const colStyle = (width: number, isLast = false): React.CSSProperties => ({
    width, flexShrink: 0, display: 'flex', flexDirection: 'column',
    borderRight: isLast ? 'none' : '1px solid var(--color-border-tertiary)',
  });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden', background: 'var(--color-background-primary)' }}>

      {/* COL 1: Systems */}
      <div style={colStyle(200)}>
        <ColHeader label="Sistemas" onNew={() => setSystemModal('create')} />
        <SearchInput value={qSystems} onChange={setQSystems} placeholder="Buscar sistema..." />
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {loadingSystems ? <Spinner />
          : filteredSystems.length === 0 ? <EmptyState icon="⬡" text={qSystems ? 'Sin resultados' : 'Sin sistemas'} action={!qSystems ? <SmallBtn label="Crear" onClick={() => setSystemModal('create')} primary /> : undefined} />
          : filteredSystems.map(sys => (
            <ItemRow
              key={sys.id}
              label={sys.name}
              sublabel={(() => {
                const plat = sys.platform ? { web: '🌐 Web', mobile: '📱 Móvil', desktop: '🖥 Desktop', api: '⚡ API' }[sys.platform] : null;
                const ver = sys.version ? `v${sys.version}` : null;
                return [plat, ver, sys.base_url].filter(Boolean).join(' · ') || undefined;
              })()}
              icon="⬡"
              selected={selectedSystem?.id === sys.id}
              onClick={() => setSelectedSystem(sys)}
              onEdit={() => setSystemModal(sys)}
              onDelete={() => handleDeleteSystem(sys)}
            />
          ))}
        </div>
      </div>

      {/* COL 2: Versions */}
      <div style={colStyle(190)}>
        <ColHeader label="Versiones" onNew={() => setVersionModal('create')} disabled={!selectedSystem} />
        {selectedSystem && <SearchInput value={qVersions} onChange={setQVersions} placeholder="Buscar versión..." />}
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {!selectedSystem ? <EmptyState icon="←" text="Selecciona un sistema" />
          : loadingVersions ? <Spinner />
          : filteredVersions.length === 0 ? <EmptyState icon="⧉" text={qVersions ? 'Sin resultados' : 'Sin versiones'} action={!qVersions ? <SmallBtn label="Crear" onClick={() => setVersionModal('create')} primary /> : undefined} />
          : filteredVersions.map(ver => {
            const vc = VERSION_STATUS_CFG[ver.status] ?? VERSION_STATUS_CFG.draft;
            const selected = selectedVersion?.id === ver.id;
            return (
              <div key={ver.id} onClick={() => setSelectedVersion(ver)} style={{
                padding: '10px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                border: `1px solid ${selected ? '#3B8BD4' : 'var(--color-border-tertiary)'}`,
                background: selected ? '#E6F1FB22' : 'var(--color-background-primary)',
                transition: 'all 0.15s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>⧉</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ver.name}</div>
                    <div style={{ marginTop: 4 }}><Badge text={vc.label} color={vc.color} bg={vc.bg} /></div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleCloneVersion(ver)} title="Clonar versión" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>⧉</button>
                    {ver.status !== 'active' && (
                      <button onClick={() => handleActivateVersion(ver)} title="Activar versión" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✓</button>
                    )}
                    <button onClick={() => setVersionModal(ver)} title="Editar" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✎</button>
                    <button onClick={() => handleDeleteVersion(ver)} title="Eliminar" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* COL 3: Requirements */}
      <div style={colStyle(200)}>
        <ColHeader label="Requerimientos" onNew={() => setReqModal('create')} disabled={!selectedVersion} />
        {selectedVersion && canEditReqs && (
          <div style={{ padding: '0 12px 6px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowJiraImport(true)}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}
            >
              🔗 Importar desde Jira
            </button>
          </div>
        )}
        {selectedVersion && <SearchInput value={qReqs} onChange={setQReqs} placeholder="Buscar req..." />}
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {!selectedVersion ? <EmptyState icon="←" text="Selecciona una versión" />
          : loadingReqs ? <Spinner />
          : filteredReqs.length === 0 ? <EmptyState icon="◻" text={qReqs ? 'Sin resultados' : 'Sin requerimientos'} action={!qReqs ? <SmallBtn label="Crear" onClick={() => setReqModal('create')} primary /> : undefined} />
          : filteredReqs.map(req => {
            const pc = PRIORITY_CFG[req.priority] ?? PRIORITY_CFG.medium;
            return (
              <ItemRow key={req.id} label={req.title} sublabel={req.external_ref ?? undefined}
                badge={<Badge text={pc.label} color={pc.color} bg={pc.bg} />}
                selected={selectedReq?.id === req.id} onClick={() => setSelectedReq(req)}
                onEdit={() => setReqModal(req)} onDelete={() => handleDeleteReq(req)} />
            );
          })}
        </div>
      </div>

      {/* COL 4: Scenarios */}
      <div style={colStyle(210)}>
        <ColHeader label="Escenarios" onNew={() => setScenarioModal('create')} disabled={!selectedReq} />
        {selectedReq && <SearchInput value={qScenarios} onChange={setQScenarios} placeholder="Buscar escenario..." />}
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {!selectedReq ? <EmptyState icon="←" text="Selecciona un requerimiento" />
          : loadingScenarios ? <Spinner />
          : filteredScenarios.length === 0 ? <EmptyState icon="◈" text={qScenarios ? 'Sin resultados' : 'Sin escenarios'} action={!qScenarios ? <SmallBtn label="Crear" onClick={() => setScenarioModal('create')} primary /> : undefined} />
          : filteredScenarios.map(sc => (
            <ItemRow key={sc.id} label={sc.title} sublabel={sc.description ?? undefined} icon="◈"
              selected={selectedScenario?.id === sc.id} onClick={() => setSelectedScenario(sc)}
              onEdit={() => setScenarioModal(sc)} onDelete={() => handleDeleteScenario(sc)} />
          ))}
        </div>
      </div>

      {/* RIGHT: Scenario detail (cases + AI) or Case detail (steps) */}
      {selectedScenario && !selectedCase && (
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          <ScenarioDetailPanel
            onDatasetColumnsChange={setScenarioDatasetColumns}
            scenario={selectedScenario}
            system={selectedSystem ?? undefined}
            requirementTitle={selectedReq?.title ?? ''}
            onClose={() => setSelectedScenario(null)}
            onCasesCreated={handleAICasesCreated}
            onSelectCase={setSelectedCase}
            caseList={caseList}
            loadingCases={loadingCases}
            onNewCase={() => setCaseModal('create')}
            onEditCase={tc => setCaseModal({
                ...tc,
                criticality: (['critical','high','medium','low'].includes(tc.criticality) ? tc.criticality : 'medium') as 'critical'|'high'|'medium'|'low',
              })}
            onDeleteCase={handleDeleteCase}
            onDuplicateCase={handleDuplicateCase}
            selectedCase={selectedCase}
          />
        </div>
      )}

      {selectedCase && (
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          <CaseDetailPanel
            tc={selectedCase}
            scenarioId={selectedScenario?.id ?? ''}
            scenarioTitle={selectedScenario?.title ?? ''}
            scenarioDatasetColumns={scenarioDatasetColumns}
            onClose={() => setSelectedCase(null)}
            onCasesCreated={handleAICasesCreated}
            versionId={selectedVersion?.id}
          />
        </div>
      )}

      {/* Modals */}
      {systemModal !== null && <SystemModal initial={systemModal === 'create' ? undefined : systemModal} onSave={systemModal === 'create' ? handleCreateSystem : handleEditSystem} onClose={() => setSystemModal(null)} />}
      {versionModal !== null && <VersionModal initial={versionModal === 'create' ? undefined : versionModal} onSave={versionModal === 'create' ? handleCreateVersion : handleEditVersion} onClose={() => setVersionModal(null)} />}
      {reqModal !== null && <RequirementModal initial={reqModal === 'create' ? undefined : reqModal} onSave={reqModal === 'create' ? handleCreateReq : handleEditReq} onClose={() => setReqModal(null)} />}
      {scenarioModal !== null && <ScenarioModal initial={scenarioModal === 'create' ? undefined : scenarioModal} onSave={scenarioModal === 'create' ? handleCreateScenario : handleEditScenario} onClose={() => setScenarioModal(null)} />}
      {caseModal !== null && selectedScenario && <CaseModal initial={caseModal === 'create' ? undefined : caseModal as Partial<TestCase>} onSave={caseModal === 'create' ? handleCreateCase : handleEditCase} onClose={() => setCaseModal(null)} />}
      {showJiraImport && selectedVersion && (
        <JiraImportModal
          versionId={selectedVersion.id}
          onClose={() => setShowJiraImport(false)}
          onImported={() => { setShowJiraImport(false); reloadReqs(selectedVersion.id); notify('Requerimientos importados desde Jira ✓'); }}
        />
      )}

      {deleteConfirm && (
        <DeleteImpactModal
          entityType={deleteConfirm.entityType}
          entityName={deleteConfirm.entityName}
          impact={deleteConfirm.impact}
          onConfirm={async () => { setDeleteConfirm(null); await deleteConfirm.onConfirm(); }}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a18', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 500 }}>
          {toast}
        </div>
      )}
    </div>
  );
}